import React, { useRef, useState } from "react";

/** Default WebP quality (0–1) used when re-encoding uploads. */
const WEBP_QUALITY = 0.9;

/** Decode a File into something canvas can draw, applying EXIF orientation when possible. */
async function decodeImage(file) {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch (_) {
      try {
        return await createImageBitmap(file);
      } catch (_) {
        /* fall through to <img> */
      }
    }
  }
  return await new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });
}

/**
 * Re-encode any browser-decodable image (png/jpg/jpeg/avif/gif/…) to a WebP Blob.
 * Returns null if the browser can't decode the source or can't encode WebP, so the
 * caller can fall back to uploading the original file.
 */
async function convertToWebp(file, quality = WEBP_QUALITY) {
  if (file.type === "image/webp") return file; // already WebP
  try {
    const bitmap = await decodeImage(file);
    const width = bitmap.width || bitmap.naturalWidth;
    const height = bitmap.height || bitmap.naturalHeight;
    if (!width || !height) return null;

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0, width, height);
    if (typeof bitmap.close === "function") bitmap.close();

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/webp", quality)
    );
    // toBlob yields null (or a non-webp type) if WebP encoding isn't supported.
    if (!blob || blob.type !== "image/webp") return null;
    return blob;
  } catch (err) {
    console.warn("WebP conversion failed, uploading original.", err);
    return null;
  }
}

/**
 * Image field that accepts either a pasted URL or a direct file upload.
 *
 * On upload the file is converted to WebP in the browser (with a graceful fallback
 * to the original format if conversion isn't possible), then pushed through the
 * existing admin image pipeline: POST /api/admin/presigned-image-url ->
 * { putUrl, publicUrl } against the public image bucket (assets.savingsatlas.com),
 * PUT the file to putUrl, then store the returned publicUrl.
 */
export default function ImageInput({
  value,
  onChange,
  keyHint = "image",
  placeholder = "Image URL",
  className = "",
}) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please choose an image file.");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setError("");
    setUploading(true);
    try {
      // Convert to WebP up front; fall back to the original if unsupported.
      const webp = await convertToWebp(file);
      const uploadBlob = webp || file;
      const contentType = uploadBlob.type || file.type || "application/octet-stream";
      const ext = webp
        ? "webp"
        : (file.name.split(".").pop() || "png").toLowerCase().replace(/[^a-z0-9]/g, "") || "png";

      const safeHint =
        (keyHint || "image")
          .toString()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/(^-|-$)/g, "")
          .slice(0, 40) || "image";
      const keyName = `pages/${safeHint}-${Date.now()}.${ext}`;

      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}/admin/presigned-image-url`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
          body: JSON.stringify({ keyName }),
        }
      );
      if (!res.ok) throw new Error("Failed to get upload URL");
      const { putUrl, publicUrl } = await res.json();

      const putRes = await fetch(putUrl, {
        method: "PUT",
        body: uploadBlob,
        headers: { "Content-Type": contentType },
      });
      if (!putRes.ok) throw new Error("Upload failed");

      onChange(publicUrl);
    } catch (err) {
      console.error("Image upload error", err);
      setError("Upload failed — you can paste a URL instead.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className={className}>
      <div className="flex gap-2 items-center">
        <input
          type="url"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1 border border-gray-300 rounded p-1 text-sm"
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="text-xs whitespace-nowrap px-2 py-1 rounded border border-[#a0d2cc] text-[#4d8a82] hover:bg-[#eef6f5] disabled:opacity-50"
        >
          {uploading ? "Uploading…" : value ? "Replace" : "Upload"}
        </button>
        {value && !uploading && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="text-xs whitespace-nowrap px-2 py-1 rounded border border-gray-300 text-gray-500 hover:bg-gray-50"
          >
            Clear
          </button>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFile}
        />
      </div>
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
}
